import React, { useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { useSkillRun } from '../../hooks/useSkillRun';

// The skill RUNNER — the chat where a saved skill actually gets used. The turn
// loop lives in useSkillRun, so this modal and the employee portal's run
// drawer cannot disagree about how a run behaves; this file is presentation.
// Shared by the marketplace detail modal and the Module 4 vetting card; when
// `onLogRun` is provided (vetting), the run can be logged right from here.

const LOG_OUTCOMES: { v: string; label: string; cls: string }[] = [
  { v: 'worked', label: 'Worked', cls: 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' },
  { v: 'partial', label: 'Partly', cls: 'border-amber-300 text-amber-700 hover:bg-amber-50' },
  { v: 'failed', label: "Didn't work", cls: 'border-red-300 text-red-700 hover:bg-red-50' },
];

const RunSkillModal: React.FC<{
  skill: { id: string; name: string };
  onClose: () => void;
  onBack?: () => void;
  onLogRun?: (outcome: string, note: string) => Promise<void> | void;
}> = ({ skill, onClose, onBack, onLogRun }) => {
  const { messages, needsApproval, gmailEnabled, busy, error, send, approveTool } = useSkillRun(skill.id);
  const [input, setInput] = useState('');
  const [logNote, setLogNote] = useState('');
  const [logging, setLogging] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, busy]);

  const submit = async () => {
    const text = input;
    setInput('');
    await send(text);
  };
  const approveGmail = () => approveTool('gmail_read');
  const logRun = async (outcome: string) => {
    if (!onLogRun) return;
    setLogging(true);
    try {
      await onLogRun(outcome, logNote.trim());
      onClose();
    } finally { setLogging(false); }
  };

  return (
    <div className="fixed inset-0 z-[195] bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl p-5 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h2 className="text-base font-semibold text-gray-900">Run · {skill.name}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <div className="flex items-center justify-between mb-2">
          {onBack && (
            <button onClick={onBack} className="text-[12px] font-semibold text-gray-500 hover:text-brand-600">← Back to the skill</button>
          )}
          {gmailEnabled && (
            <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-0.5">
              Reading your Gmail · read-only
            </span>
          )}
        </div>
        {needsApproval.includes('gmail_read') && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 mb-2 flex items-center gap-2 flex-wrap">
            <span className="text-[12.5px] text-amber-800 flex-1 min-w-[200px]">
              This skill can read your Gmail — you chose “only with approval” in your tool dive.
            </span>
            <button onClick={approveGmail} disabled={busy}
              className="text-[12px] font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-lg px-3 py-1.5 disabled:opacity-50">
              Allow for this run
            </button>
          </div>
        )}
        <div className="h-[380px] overflow-y-auto space-y-2.5 bg-gray-50 rounded-lg p-3 mb-2.5">
          {messages.map((m, i) => (
            <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
              <div className={`max-w-[85%] rounded-lg px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'bg-brand-500 text-white' : 'bg-white border border-gray-100 text-gray-800'}`}>
                {m.content}
              </div>
            </div>
          ))}
          {busy && <div className="flex justify-start"><div className="bg-white border border-gray-100 rounded-lg px-3 py-2"><Loader2 size={14} className="animate-spin text-gray-400" /></div></div>}
          {error && <p className="text-[12px] text-red-500">{error}</p>}
          <div ref={endRef} />
        </div>
        <div className="flex items-end gap-2">
          <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={2}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void submit(); } }}
            placeholder="Your input for this step…"
            className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand-200" />
          <button onClick={submit} disabled={busy || !input.trim()}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-lg px-4 py-2 disabled:opacity-50 whitespace-nowrap">
            Send
          </button>
        </div>
        {onLogRun && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-[11.5px] text-gray-500 mb-1.5">Done running it? Log how it went, that's what vets this skill.</p>
            <div className="flex items-center gap-1.5 flex-wrap">
              <input value={logNote} onChange={(e) => setLogNote(e.target.value)}
                placeholder="What happened? What to fix next?"
                className="flex-1 min-w-[160px] rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12px] focus:outline-none focus:ring-2 focus:ring-brand-200" />
              {LOG_OUTCOMES.map((o) => (
                <button key={o.v} onClick={() => logRun(o.v)} disabled={logging || busy}
                  className={`inline-flex items-center gap-1 rounded-full border bg-white px-2.5 py-1 text-[12px] font-medium disabled:opacity-50 ${o.cls} whitespace-nowrap`}>
                  {logging && <Loader2 size={12} className="animate-spin" />} {o.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RunSkillModal;
